import { type ComponentProps, useId } from 'react';

import { cn } from '../lib/cn';

/**
 * 앰비언트 플로우 라인 — D1 §7-2. 제품에서 유일하게 허용되는 장식이다.
 *
 * 네이비→오렌지 그라디언트 곡선 3줄이 서로 다른 속도·방향으로 흐른다.
 * **눈에 띄면 실패한 것이다** — 불투명도 0.55 / 0.35 / 0.22, 주기 26s / 34s / 20s.
 *
 * 랜딩에만 쓴다. 로그인 후 화면에는 쓰지 않는다 — 작업대는 조용해야 한다.
 * 애니메이션은 `@media (prefers-reduced-motion: no-preference)`로 감싸 옵트인이다.
 */
export function FlowField({ className, ...props }: ComponentProps<'svg'>) {
  // 한 페이지에 두 번 놓여도 그라디언트가 충돌하지 않도록 id를 격리한다.
  const uid = useId().replace(/:/g, '');
  const g1 = `flow-a-${uid}`;
  const g2 = `flow-b-${uid}`;

  return (
    <svg
      className={cn('pointer-events-none absolute inset-0 size-full', className)}
      viewBox="0 0 1600 900"
      preserveAspectRatio="none"
      aria-hidden="true"
      {...props}
    >
      <defs>
        <linearGradient id={g1} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--color-brand-600)" />
          <stop offset="100%" stopColor="var(--color-accent-500)" />
        </linearGradient>
        <linearGradient id={g2} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--color-accent-500)" />
          <stop offset="100%" stopColor="var(--color-brand-600)" />
        </linearGradient>
      </defs>

      <path
        className="lf-flow-path lf-flow-path-1"
        d="M -100 620 C 250 520, 420 720, 760 560 S 1250 380, 1700 520"
        fill="none"
        strokeLinecap="round"
        stroke={`url(#${g1})`}
        strokeWidth={1.6}
        opacity={0.55}
      />
      <path
        className="lf-flow-path lf-flow-path-2"
        d="M -100 300 C 220 420, 480 200, 820 340 S 1300 480, 1700 300"
        fill="none"
        strokeLinecap="round"
        stroke={`url(#${g2})`}
        strokeWidth={1.2}
        opacity={0.35}
      />
      <path
        className="lf-flow-path lf-flow-path-3"
        d="M -100 780 C 300 700, 560 860, 900 740 S 1400 620, 1700 760"
        fill="none"
        strokeLinecap="round"
        stroke="var(--color-accent-500)"
        strokeWidth={2}
        opacity={0.22}
      />
    </svg>
  );
}
